<script setup>
import { computed, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { isFeatureRestrictedForAccount } from 'dashboard/helper/restrictedFeatures';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const currentUser = computed(() => store.getters.getCurrentUser);
const currentRole = computed(() => store.getters.getCurrentRole);
const currentAccountId = computed(() => Number(store.getters.getCurrentAccountId));
const currentAccount = computed(() => {
  const accountGetter = store.getters['accounts/getAccount'];
  if (typeof accountGetter !== 'function' || !currentAccountId.value) {
    return null;
  }

  return accountGetter(currentAccountId.value);
});

const hideBlockContactAction = computed(() =>
  isFeatureRestrictedForAccount(
    currentAccount.value,
    'conversation_actions_hide_block_contact',
    {
      surface: 'frontend',
      currentUser: currentUser.value,
      currentRole: currentRole.value,
    }
  )
);

const hideSendTranscriptAction = computed(() =>
  isFeatureRestrictedForAccount(
    currentAccount.value,
    'conversation_actions_hide_send_transcript',
    {
      surface: 'frontend',
      currentUser: currentUser.value,
      currentRole: currentRole.value,
    }
  )
);

const actionMenuItems = computed(() => {
  const items = [];

  if (!hideBlockContactAction.value) {
    if (!currentChat.value.muted) {
      items.push({
        icon: 'i-lucide-volume-off',
        label: t('CONTACT_PANEL.MUTE_CONTACT'),
        action: 'mute',
        value: 'mute',
      });
    } else {
      items.push({
        icon: 'i-lucide-volume-1',
        label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
        action: 'unmute',
        value: 'unmute',
      });
    }
  }

  if (!hideSendTranscriptAction.value) {
    items.push({
      icon: 'i-lucide-share',
      label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
      action: 'send_transcript',
      value: 'send_transcript',
    });
  }

  return items;
});

const hasActionMenuItems = computed(() => actionMenuItems.value.length > 0);

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute' && !hideBlockContactAction.value) {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute' && !hideBlockContactAction.value) {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript' && !hideSendTranscriptAction.value) {
    toggleEmailModal();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  if (hideBlockContactAction.value) {
    return;
  }

  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  if (hideBlockContactAction.value) {
    return;
  }

  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

const openTranscriptModal = () => {
  if (hideSendTranscriptAction.value) {
    return;
  }

  toggleEmailModal();
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, openTranscriptModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, openTranscriptModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-if="hasActionMenuItems"
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
